import { useEffect, useRef, type RefObject } from "react"
import { Formik, getIn, useFormikContext } from "formik"
import { CharField, SingleSelectField } from "@inmagik/magik-ui"
import * as yup from "yup"
import type { BlockBase, Media, TextImageBlock, TextPosition, MediaFit } from "../../../../types"
import { MediaThumb } from "../Previews"
import { mediaFitOptions, mediaPositionOptions, textPositionOptions } from "../../options"
import { AutoValidate } from "./Autovalidate"
import { WysiwygField } from "../../../../components/WysiwygField"
import { MediaSelectionModal } from "../MediaSelectionModal"

function SingleMediaPicker() {
  const { values, setFieldValue } = useFormikContext<TextImageBlock>()

  const selectMedia = (doc: Media) => {
    setFieldValue("mediaId", doc.id)
    setFieldValue("mediaTitle", doc.title ?? "")
    setFieldValue("mediaThumbnail", String(doc.thumbnail ?? doc.media_file ?? ""))
    setFieldValue("mediaFile", String(doc.media_file ?? ""))
  }

  return (
    <MediaSelectionModal
      buttonLabel={values.mediaId ? "Cambia media" : "Seleziona media"}
      title="Seleziona media"
      selectionSummary={values.mediaId ? "1 immagine selezionata." : ""}
      renderSelectionCell={(doc) => (
        <input type="radio" name="mediaId" checked={values.mediaId === doc.id} onChange={() => selectMedia(doc)} />
      )}
      onCreateAndSelect={selectMedia}
    />
  )
}

export function TextImageBlockForm({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: BlockBase
  saveBlock: (b: BlockBase) => void
  setBlockFormValid: (v: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    slideTitle: yup.string().required("Il titolo della slide è obbligatorio"),
    mediaId: yup.number().required("Seleziona un'immagine"),
    caption: yup.string().nullable(),
    title: yup.string().nullable(),
    content: yup.string().required("Il testo è obbligatorio"),
    textPosition: yup.string().oneOf(["left", "right"]).required("Seleziona la posizione del testo"),
    fit: yup.string().oneOf(["fit", "cover"]).required("Seleziona fit/cover"),
  })

  const hiddenSubmitRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!submitRef?.current) return
    submitRef.current.onclick = () => hiddenSubmitRef.current?.click()
  }, [submitRef])

  const initialValues: TextImageBlock = {
    uuid: block.uuid,
    blockType: "TextImage",
    slideTitle: (block as TextImageBlock).slideTitle ?? "",
    mediaId: (block as TextImageBlock).mediaId ?? undefined,
    mediaThumbnail: (block as TextImageBlock).mediaThumbnail ?? undefined,
    mediaTitle: (block as TextImageBlock).mediaTitle ?? undefined,
    mediaFile: (block as TextImageBlock).mediaFile ?? undefined,
    caption: (block as TextImageBlock).caption ?? "",
    title: (block as TextImageBlock).title ?? "",
    content: (block as TextImageBlock).content ?? "",
    textPosition: (block as TextImageBlock).textPosition ?? "left",
    fit: (block as TextImageBlock).fit ?? "fit",
    position: (block as TextImageBlock).position ?? "center center",
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => saveBlock(values)}
    >
      {({ handleSubmit, values, errors, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <CharField
            label="Titolo slide"
            layout="horizontal"
            value={values.slideTitle}
            onChange={(v) => setFieldValue("slideTitle", v)}
            required
            isValid={!getIn(errors, "slideTitle")}
            errorMessage={getIn(errors, "slideTitle")}
            className="mb-3"
          />

          <div className="mb-3">
            <div className="d-flex align-items-center flex-column gap-2 mb-3 w-100">
              <MediaThumb
                thumbnail={values.mediaThumbnail}
                title={values.mediaTitle}
                fit={values.fit}
                width={300}
                height={200}
                position={values.position}
              />
              <div className="mb-2">
                <strong>Selezionata:</strong>{" "}
                {values.mediaId ? (
                  <>
                    ID {values.mediaId}
                    {values.mediaTitle ? ` — ${values.mediaTitle}` : ""}
                  </>
                ) : (
                  <span className="text-muted">nessuna</span>
                )}
              </div>
              <div className="mb-3">
                <SingleMediaPicker />
              </div>
            </div>

            <div className="flex-grow-1">
              <div className="mb-2">
                <SingleSelectField
                  label="Fit"
                  layout="horizontal"
                  options={mediaFitOptions}
                  value={mediaFitOptions.find((o) => o.value === values.fit)}
                  onChange={(opt) => opt && setFieldValue("fit", opt.value as MediaFit)}
                  errorMessage={getIn(errors, "fit")}
                />
              </div>
              {values.fit === "cover" && (
                <div className="mb-2">
                  <SingleSelectField
                    label="Posizione immagine"
                    layout="horizontal"
                    options={mediaPositionOptions}
                    value={mediaPositionOptions.find((o) => o.value === values.position)}
                    onChange={(opt) => opt && setFieldValue("position", opt.value)}
                    errorMessage={getIn(errors, "position")}
                    
                  />
                </div>
              )}
              <div className="mb-2">
                <WysiwygField
                  label="Didascalia immagine"
                  layout="horizontal"
                  value={values.caption || ""}
                  onChange={(v) => setFieldValue("caption", v)}
                  isValid={!getIn(errors, "caption")}
                  errorMessage={getIn(errors, "caption")}
                  className="mb-2"
                  placeholder="(opzionale)"
                  rows={6}
                />
              </div>
            </div>
          </div>

          <CharField
            label="Titolo (opzionale)"
            layout="horizontal"
            value={values.title || ""}
            onChange={(v) => setFieldValue("title", v)}
            isValid={!getIn(errors, "title")}
            errorMessage={getIn(errors, "title")}
            className="mb-3"
            placeholder="(opzionale)"
          />

          <WysiwygField
            label="Testo"
            layout="horizontal"
            value={values.content}
            onChange={(v) => setFieldValue("content", v)}
            required
            isValid={!getIn(errors, "content")}
            errorMessage={getIn(errors, "content")}
            className="mb-3"
          />

          <div className="mb-2">
            <SingleSelectField
              label="Posizione testo"
              layout="horizontal"
              options={textPositionOptions}
              value={textPositionOptions.find((o) => o.value === values.textPosition)}
              onChange={(opt) => opt && setFieldValue("textPosition", opt.value as TextPosition)}
              errorMessage={getIn(errors, "textPosition")}
            />
          </div>

          <button ref={hiddenSubmitRef} type="submit" style={{ display: "none" }} />
          <AutoValidate setBlockFormValid={setBlockFormValid} />
        </form>
      )}
    </Formik>
  )
}
